import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { Location } from "@angular/common";
import { FormBuilder, FormGroup, FormControl, Validators } from "@angular/forms";
import { MatDialog, MatDialogConfig, MatTabGroup } from "@angular/material";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, tap, switchMap, takeUntil } from "rxjs/operators";

import { EnquiryService } from "../shared/enquiry.service";
import { SnackBarService } from "../../core/snack-bar.service";
import { ValidationHelperService } from "../../core/validation-helper.service";

import { atLeastOne } from "../../core/at-least-one-validator";
import { DownloadQuoteDialogComponent } from "../download-quote-dialog/download-quote-dialog.component";
import { DropdownOption } from "../../shared/dropdown-option.model";
import { EmailEnquiryDialogComponent } from "../email-enquiry-dialog/email-enquiry-dialog.component";
import { Enquiry } from "../shared/enquiry.model";
import { EnquiryOptions } from "../shared/enquiry-options.model";
import { IntroducerRateChart } from "../shared/introducer-rate-chart.model";
import { RepaymentCalculation } from "../shared/repayment-calculation.model";
import { RepaymentOption } from "../shared/repayment-option.model";

@Component({
    template: require('./enquiry-detail.component.html'),
    styles: [require('./enquiry-detail.component.css')]
})
export class EnquiryDetailComponent implements OnInit, OnDestroy {
    enquiry: Enquiry;
    enquiryForm: FormGroup;
    opts: EnquiryOptions;
    errorMessage: string;
    filteredIrcs: IntroducerRateChart[];
    loadingEnquiryOptions: boolean;
    loadingRepaymentOptions: boolean;
    loadingSave: boolean;
    loadingSubmission: boolean;
    programFilterFC: FormControl;
    selectedIrc: IntroducerRateChart;
    repaymentOptionFC: FormControl;
    uniquePrograms: DropdownOption[];

    private currencyInputRegex = /^[1-9]\d*(\.\d+)?$/; // Temp (until we can use number input instead of text) currency input (no leading zeroes). Use this - https://regexr.com/
    private ngUnsubscribe: Subject<void> = new Subject(); //see https://stackoverflow.com/a/41177163
    
    // the payment frequency tab group is not created until the repayment options are loaded, so we need 
    // this setter to init the ViewChild property (it is called automatically when the relevent *ngIf is true)
    // see https://stackoverflow.com/a/41095677
    private frequencyTabGroup: MatTabGroup;
    @ViewChild(MatTabGroup) set content(content: MatTabGroup) {
        this.frequencyTabGroup = content;
    }

    // using BehaviorSubject since we need to be able to push the current repaymentOpts into the email/download quote dialogs when they are opened.
    // see section on BehaviorSubject - http://blog.angular-university.io/how-to-build-angular2-apps-using-rxjs-observable-data-services-pitfalls-to-avoid/
    private _repaymentOptions$: BehaviorSubject<RepaymentOption[]> = new BehaviorSubject([]);
    public readonly repaymentOptions$: Observable<RepaymentOption[]> = this._repaymentOptions$.asObservable();
    
    constructor(
        private dialog: MatDialog,
        private enquiryService: EnquiryService,
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBarService: SnackBarService,
        private validationHelperService: ValidationHelperService) {

        this.programFilterFC = new FormControl();
        this.repaymentOptionFC = new FormControl();
        this.createForm();
    }

    ngOnInit() {
        this.loadingEnquiryOptions = true;

        // get the enquiryOptions, and then the existing enquiry (or a default one if new)
        this.enquiryService.getOptions()
            .then(data => {
                this.opts = data;
                
                if (this.opts.introducerRateCharts.length === 0) {
                    throw new Error("No finance types have been made available to this supplier - enquiries cannot be created.");
                }
                
                this.uniquePrograms = Array.from( // make an array of DropdownOption from this.introducerRateCharts
                    new Set(this.opts.introducerRateCharts
                        .map(irc => {
                            return {
                                id: irc.programID,
                                label: irc.programName,
                            } as DropdownOption
                        })
                        .filter((opt, index, self) => // then remove the duplicate objects https://stackoverflow.com/a/36744732
                            index === self.findIndex((o) => (
                                o.id === opt.id
                            )))
                        .sort((a, b) => a.label.localeCompare(b.label)) // es6 sort https://stackoverflow.com/a/35092754
                    )
                );

                this.activatedRoute.paramMap
                    .pipe(
                        takeUntil(this.ngUnsubscribe),
                        switchMap((params: ParamMap) => this.enquiryService.getEnquiry(+params.get("id"))) // (+) converts string "id" to a number
                    )
                    .subscribe(enq => {
                        this.loadingEnquiryOptions = false;
                        this.enquiry = enq;

                        this.enquiryForm.patchValue(this.enquiry);
                        this.financeFG.patchValue(this.enquiry);
                        this.phoneNumbersFG.get("phone").setValue(this.enquiry.phoneNumber);
                        this.phoneNumbersFG.get("mobile").setValue(this.enquiry.mobileNumber);

                        // The vendor program filter dropdown doesn't actually get saved with the rest of the form,
                        // it's just a presentation component that filters the "Finance Type" (introducerRateChartID) dropdown for the user.
                        // Having to split this into two dropdowns (Vendor Program / Finance Type), even though its just a single object (IntroducerRateChart)
                        // has resulted in a bit of extra logic, but FAW have asked for it this way as it may be easier for the user.
                        const savedIrc = this.opts.introducerRateCharts.find(irc => irc.id === this.enquiry.introducerRateChartID);
                        const firstIrcUsingDefaultProduct = this.opts.introducerRateCharts.filter(i => i.productID === this.opts.defaultProductID)[0];

                        let programID: number;
                        if (savedIrc) {
                            programID = savedIrc.programID
                        } else if (firstIrcUsingDefaultProduct) {
                            programID = firstIrcUsingDefaultProduct.programID;
                        } else {
                            programID = this.uniquePrograms[0].id;
                        }

                        this.updateFilteredIrcs(programID);
                        this.programFilterFC.setValue(programID, { emitEvent: false });
                        if (!this.enquiry.id && this.filteredIrcs.length > 0) {
                            this.financeFG.get("introducerRateChartID").setValue(this.filteredIrcs[0].id);
                        }

                        if (this.enquiry.submittedOn != null) {
                            this.programFilterFC.disable({ emitEvent: false });
                            this.enquiryForm.disable({ emitEvent: false });
                        }
                    },
                    err => this.snackBarService.openSnackBar("Error retrieving enquiry."));
            })
            .catch(err => {
                this.loadingEnquiryOptions = false;
                this.errorMessage = err;
            });

        // when a vendor program is selected, filter the introducerRateChartID dropdown and select the first one
        this.programFilterFC.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(val => {
                this.updateFilteredIrcs(val);
                this.financeFG.get("introducerRateChartID").setValue(this.filteredIrcs[0].id);
            });

        // update form validators and assetTypeGroup dropdown when the introducerRateChart dropdown changes
        this.financeFG.get("introducerRateChartID").valueChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(val => {
                this.selectedIrc = this.opts.introducerRateCharts.find(i => i.id === val);

                if (this.selectedIrc && this.checkSelectedIrcConfig()) {
                    const assetTypeGroupFC = this.financeFG.get("assetTypeGroupID") as FormControl;
                    if (!this.selectedIrc.assetTypeGroups.find(a => a.id === assetTypeGroupFC.value)) {
                        assetTypeGroupFC.setValue(this.selectedIrc.assetTypeGroups[0].id);
                    }
                    
                    this.costOfGoodsFC.setValidators(
                        [
                            Validators.required,
                            Validators.pattern(this.currencyInputRegex),
                            Validators.min(this.selectedIrc.minCostOfGoods),
                            Validators.max(this.selectedIrc.maxCostOfGoods)
                        ]);
                    this.costOfGoodsFC.updateValueAndValidity();

                    const isAdvanceFC = this.financeFG.get("isAdvance") as FormControl;
                    isAdvanceFC.setValue(this.selectedIrc.allowInAdvance);
                    (this.selectedIrc.allowInAdvance && this.selectedIrc.allowInArrears)
                        ? isAdvanceFC.enable()
                        : isAdvanceFC.disable();
                }
            });

        // automatically get repayment options when the finance area of the form is valid
        this.financeFG.valueChanges
            .pipe(
                takeUntil(this.ngUnsubscribe),
                debounceTime(500), //TODO: debounce also happens on initial load when opening an existing enquiry..
                distinctUntilChanged(), //TODO: doesnt do anything?
                switchMap(val => {
                    this.repaymentOptionFC.reset();

                    if (this.financeFG.valid) {
                        this.loadingRepaymentOptions = true;
                        return this.getRepaymentOptions();
                    } else {
                        return of<RepaymentOption[]>([]);
                    }
                }),
                tap(opts => {
                    this.loadingRepaymentOptions = false;
                    this._repaymentOptions$.next(opts);

                    // if opening an existing enquiry, search for a matching repayment option and 'select' it
                    const selectedOpt = opts.find(r =>
                        r.paymentFrequencyID === this.enquiry.paymentFrequencyID
                        && r.repayment === this.enquiry.repayment
                        && r.term === this.enquiry.term
                        && r.residualAmount === this.enquiry.residualAmount);

                    if (selectedOpt) {
                        this.repaymentOptionFC.setValue(selectedOpt);

                        // ensure the correct payment frequency tab is active on load
                        if (this.frequencyTabGroup) {
                            this.frequencyTabGroup.selectedIndex = this.selectedIrc.paymentFrequencies.findIndex(f => f.id == selectedOpt.paymentFrequencyID);
                        }
                    }
                }),
                catchError(err => {
                    this.errorMessage = err;
                    return of<RepaymentOption[]>([]);
                })
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    
    actionsDisabled(): boolean {
        if (this.enquiry) {
            return (this.enquiry.submittedOn != null)
                || this.loadingEnquiryOptions
                || this.showRepaymentsSpinner()
                || this.showSavingSpinner()
                || this.showSubmissionSpinner()
                || this.errorMessage != null;
        } else {
            return true;
        }
    }

    get costOfGoodsFC(): FormControl { return this.enquiryForm.get("finance.costOfGoods") as FormControl; }
    get financeFG(): FormGroup { return this.enquiryForm.get("finance") as FormGroup; }
    get phoneNumbersFG(): FormGroup { return this.enquiryForm.get("phoneNumbers") as FormGroup; }

    checkSelectedIrcConfig(): boolean {
        var isValid =
            this.selectedIrc.terms != null
            && this.selectedIrc.terms.length > 0
            && this.selectedIrc.paymentFrequencies != null
            && this.selectedIrc.paymentFrequencies.length > 0
            && this.selectedIrc.assetTypeGroups != null
            && this.selectedIrc.assetTypeGroups.length > 0;

        this.errorMessage = isValid ? null : "Rates configuration error.";

        return isValid;
    }

    createForm(): void {
        this.enquiryForm = this.fb.group({
            // separate 'finance' formGroup so we can just validate this before getting repayment options
            finance: this.fb.group({
                introducerRateChartID: ['', Validators.required],
                assetTypeGroupID: ['', Validators.required],
                costOfGoods: ['', [Validators.required, Validators.pattern(this.currencyInputRegex)]],
                isAdvance: ['', Validators.required],
            }),

            phoneNumbers: this.fb.group({
                phone: ['', Validators.maxLength(20)],
                mobile: ['', Validators.maxLength(20)],
            }, { validator: atLeastOne(Validators.required) }),

            goodsDescription: ['', Validators.maxLength(1000)],
            contactName: ['', [Validators.required, Validators.maxLength(100)]],
            businessName: ['', [Validators.required, Validators.maxLength(100)]],
            abn: ['', [Validators.minLength(11), Validators.maxLength(11)]],
            emailAddress: ['', Validators.maxLength(100)],
            address: ['', Validators.maxLength(200)],
            notes: ['', Validators.maxLength(500)],
        });
    }

    getFilteredRepaymentOptions(paymentFrequencyID: number, opts: RepaymentOption[]): RepaymentOption[] {
        return opts.filter(r => r.paymentFrequencyID == paymentFrequencyID);
    }

    getRepaymentOptions(): Observable<RepaymentOption[]> {
        const calcParams: RepaymentCalculation = {
            introducerRateChartID: this.financeFG.get("introducerRateChartID").value,
            assetTypeGroupID: this.financeFG.get("assetTypeGroupID").value,
            costOfGoods: this.financeFG.get("costOfGoods").value,
            isAdvance: this.financeFG.get("isAdvance").value
        }

        return this.enquiryService.getRepaymentOptions(calcParams);
    }

    getResidualLabel(): string {
        return (this.selectedIrc && this.selectedIrc.labelResidualAsBalloon)
            ? "Balloon"
            : "Residual";
    }

    getSelectedRepaymentOptionIndex(opts: RepaymentOption[]): number {
        let selectedOptIndex = null;

        const selectedOpt = this.repaymentOptionFC.value as RepaymentOption;
        if (selectedOpt) {
            selectedOptIndex = opts.findIndex(r =>
                r.paymentFrequencyID === selectedOpt.paymentFrequencyID
                && r.repayment === selectedOpt.repayment
                && r.term === selectedOpt.term
                && r.residualAmount === selectedOpt.residualAmount);
        }

        return selectedOptIndex;
    }

    getSingularFrequencyDesc(paymentFreq: string): string {
        let singularDesc: string = null;
        switch (paymentFreq) {
            case 'Weekly':
                singularDesc = 'Week';
                break;
            case 'Monthly':
                singularDesc = 'Month';
                break;
            case 'Quarterly':
                singularDesc = 'Quarter';
                break;
            case 'Annual':
                singularDesc = 'Year';
                break;
            case 'Fortnightly':
                singularDesc = 'Fortnight';
                break;
            default:
                singularDesc = 'Period';
                break;
        }

        return singularDesc;
    }

    goToEnquiriesList(): void {
        this.router.navigate(["/enquiries"]);
    }

    isRepaymentOptionSelected(opt: RepaymentOption): boolean {
        let isSelected = false;

        const selectedOpt = this.repaymentOptionFC.value as RepaymentOption;
        if (selectedOpt) {
            isSelected =
                opt.paymentFrequencyID === selectedOpt.paymentFrequencyID
                && opt.repayment === selectedOpt.repayment
                && opt.term === selectedOpt.term
                && opt.residualAmount === selectedOpt.residualAmount;
        }

        return isSelected;
    }

    openDownloadQuoteDialog() {
        const opts = this._repaymentOptions$.getValue();
        const selectedOptIndex = this.getSelectedRepaymentOptionIndex(opts);

        let dialogRef = this.dialog.open(DownloadQuoteDialogComponent, {
            data: {
                enquiryID: this.enquiry.id,
                repaymentOptions: opts,
                selectedRepaymentIndex: selectedOptIndex
            }
        });
    }

    openEmailEnquiryDialog() {
        const opts = this._repaymentOptions$.getValue();
        const selectedOptIndex = this.getSelectedRepaymentOptionIndex(opts);

        let dialogRef = this.dialog.open(EmailEnquiryDialogComponent, {
            //height: '80%',
            //width: '80%',
            disableClose: true,
            autoFocus: false, // added this because iOS Safari was autofocusing the first <input type="text" /> causing immediate scrolling down to "Subject" input on the dialog
            data: {
                applicationFormAvailable: this.selectedIrc.applicationFormAvailable,
                enquiry: this.enquiry,
                repaymentOptions: opts,
                selectedRepaymentIndex: selectedOptIndex
            }
        });
    }

    // see https://angular.io/guide/reactive-forms#save-form-data
    prepareSaveEnquiry(): Enquiry {
        const formModel = this.enquiryForm.value;
        const isAdvance = this.financeFG.get("isAdvance").value as boolean; // because the FormControl may be disabled
        const selectedOpt = this.repaymentOptionFC.value as RepaymentOption;

        // assigning a few extra fields here just so we don't need to look up the ID values again on the backend, before sending the email
        const assetTypeGroup = this.selectedIrc.assetTypeGroups.find(i => i.id === formModel.finance.assetTypeGroupID);

        const saveEnquiry: Enquiry = {
            id: this.enquiry.id,
            enquiryNumber: "", // generated by backend on enquiry creation

            // from formModel.finance
            introducerRateChartID: formModel.finance.introducerRateChartID as number,
            productName: this.selectedIrc.productName,
            programName: this.selectedIrc.programName,
            assetTypeGroupID: formModel.finance.assetTypeGroupID as number,
            assetTypeGroupName: assetTypeGroup.label,
            costOfGoods: formModel.finance.costOfGoods as number,
            isAdvance: isAdvance,

            // from formModel.phoneNumbers
            phoneNumber: formModel.phoneNumbers.phone as string,
            mobileNumber: formModel.phoneNumbers.mobile as string,

            // from formModel
            goodsDescription: formModel.goodsDescription as string,
            contactName: formModel.contactName as string,
            businessName: formModel.businessName as string,
            abn: formModel.abn as string,
            emailAddress: formModel.emailAddress as string,
            address: formModel.address as string,
            notes: formModel.notes as string,

            // from selected RepaymentOption
            paymentFrequencyID: selectedOpt ? selectedOpt.paymentFrequencyID : null,
            paymentFrequencyName: selectedOpt ? selectedOpt.paymentFrequencyName : null,
            repayment: selectedOpt ? selectedOpt.repayment : null,
            term: selectedOpt ? selectedOpt.term : null,
            residualAmount: selectedOpt ? selectedOpt.residualAmount : null,
        };

        return saveEnquiry;
    }

    save(downloadQuoteClicked: boolean, emailClicked: boolean): void {
        if (this.enquiryForm.invalid) {
            this.validationHelperService.markAllTouched(this.enquiryForm);
            this.snackBarService.openSnackBar("Please fill in required values.");
            return;
        }

        this.loadingSave = true;
        const enquiryToSave = this.prepareSaveEnquiry();

        if (enquiryToSave.id != null) {
            this.enquiryService.update(enquiryToSave)
                .then(() => {
                    this.loadingSave = false;
                    this.enquiry = Object.assign({}, enquiryToSave);
                    this.snackBarService.openSnackBar("Enquiry updated.");

                    // TODO: this could be implemented better..
                    if (downloadQuoteClicked) {
                        this.openDownloadQuoteDialog();
                    } else if (emailClicked) {
                        this.openEmailEnquiryDialog();
                    }
                })
                .catch(err => {
                    this.loadingSave = false;
                    this.snackBarService.openSnackBar(err);
                });
        } else {
            this.enquiryService.create(enquiryToSave)
                .then(enq => {
                    this.loadingSave = false;
                    this.enquiry = Object.assign({}, enquiryToSave);
                    this.enquiry.id = enq.id;
                    this.snackBarService.openSnackBar("Enquiry saved.");

                    // Replace "/new" in the URL with "/{id}"
                    // This was added because of the default behaviour in iOS Safari when downloading a PDF from the email/quote dialogs.
                    // Safari will open the PDF in the current tab, and then the user would need to click the back button on their phone, so
                    // we'd need the ID in the URL to ensure the enquiry data is reloaded when they navigate back.
                    this.location.replaceState("/enquiries/" + this.enquiry.id);

                    if (downloadQuoteClicked) {
                        this.openDownloadQuoteDialog();
                    } else if (emailClicked) {
                        this.openEmailEnquiryDialog();
                    }
                })
                .catch(err => {
                    this.loadingSave = false;
                    this.snackBarService.openSnackBar(err);
                });
        }
    }

    showAssetTypeGroup(): boolean {
        return this.selectedIrc && this.selectedIrc.assetTypeGroups.length > 1;
    }

    showFinanceForm(): boolean {
        return this.enquiry != null && this.opts != null;
    }

    showFinanceInvalidMessage(): boolean {
        return this.financeFG.invalid;
    }

    showPhoneError(): boolean {
        return this.phoneNumbersFG.touched && this.phoneNumbersFG.hasError("atLeastOne");
    }

    showProgramFilter(): boolean {
        return this.uniquePrograms && this.uniquePrograms.length > 1;
    }

    showRepaymentsList(): boolean {
        return !this.loadingRepaymentOptions && this.financeFG.valid;
    }

    showRepaymentsSpinner(): boolean {
        return this.loadingRepaymentOptions && this.financeFG.valid;
    }

    showResidual(): boolean {
        return this.selectedIrc && this.selectedIrc.showResidualValue;
    }

    showSavingSpinner(): boolean {
        return this.loadingSave;
    }

    showSubmissionSpinner(): boolean {
        return this.loadingSubmission;
    }

    showSubmittedRepaymentOption(): boolean {
        return this.enquiry && (this.enquiry.submittedOn != null);
    }

    submit(): void {
        if (this.enquiryForm.invalid || !this.repaymentOptionFC.value) {
            this.validationHelperService.markAllTouched(this.enquiryForm);
            this.snackBarService.openSnackBar("Please fill in required values and select a repayment option.");
            return;
        }

        this.loadingSubmission = true;
        const enquiryToSave = this.prepareSaveEnquiry();

        this.enquiryService.submit(enquiryToSave)
            .then(() => {
                this.loadingSubmission = false;
                this.snackBarService.openSnackBar("Enquiry submitted.");
                this.goToEnquiriesList();
            })
            .catch(err => {
                this.loadingSubmission = false;
                this.snackBarService.openSnackBar(err);
            });
    }

    updateFilteredIrcs(programID: number): void {
        this.filteredIrcs = this.opts.introducerRateCharts.filter(irc => irc.programID == programID);

        // FAW always want the default product (currently Operating Lease) to appear at the top of the dropdown
        const index = this.filteredIrcs.findIndex(i => i.productID === this.opts.defaultProductID)
        if (index > -1) {
            let defaultProductIrc = this.filteredIrcs.splice(index, 1)[0];
            this.filteredIrcs.unshift(defaultProductIrc);
        }
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./src/app/enquiry/enquiry-detail/enquiry-detail.component.ts